/**
 * GoogleSignIn - Custom styled Google sign-in button
 *
 * Auth Code Flow with Popup:
 * 1. User clicks our custom AppButton
 * 2. requestGoogleAuthCode opens Google popup (GIS SDK code model)
 * 3. Google returns authorization code (safe to transmit)
 * 4. We POST code to BFF endpoint /api/auth/google
 * 5. Hono BFF exchanges code for tokens with Google (using client_secret)
 * 6. Hono BFF sends id_token to Rust backend for validation
 * 7. Rust backend validates, creates session, returns token
 * 8. Hono BFF sets HTTP-only cookie
 *
 * Requires: Cross-Origin-Opener-Policy: same-origin-allow-popups header
 *
 * W3.2 (auth-debt epic): errors ride the SAME typed reason→catalog
 * pipeline as the OTP flow (`useLocalizeApiError`) and render inline —
 * the former `onError?: (error: string)` prop carried hardcoded English
 * and had NO consumer, so every Google failure was silently dropped.
 *
 * LET-77: the popup half of the flow used to end at ONE line —
 * `setError(tGoogle('errorCancelled'))` — for every way the popup can fail.
 * With staging's consent screen set to Internal, Google answered
 * `error=org_internal` and this screen told the tester she had cancelled,
 * while the phone/OTP login that DID work went unmentioned. The popup outcome
 * is now a typed union from `@letsgethome/lib` and the mapping below is
 * exhaustive: a new outcome does not compile until this app gives it copy.
 * The two halves stay where they belong — the taxonomy is in `lib` (below the
 * apps in the DAG), the storefront's wording is here.
 */
import { useLocalizeApiError } from '@/shared/i18n/useLocalizeApiError';
import { useTranslations } from '@/shared/i18n/useTranslations';
import { bff } from '@/shared/lib/bff';
import { clientLogger } from '@/shared/lib/client-logger';
import {
  requestGoogleAuthCode,
  toGoogleAuthFailure,
  type GoogleAuthFailure,
} from '@letsgethome/lib/auth/google-oauth';
import type { UserProfile } from '@letsgethome/lib/auth/types';
import { bffError } from '@letsgethome/lib/server-errors';
import { assertNever } from '@letsgethome/lib/utils/enums';
import type { GoogleClientId, LocalizedText } from '@letsgethome/types/branded';
import { AppButton, inFlight } from '@letsgethome/ui/app-button';
import { GoogleIcon } from '@letsgethome/ui/icons/google-icon';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

interface GoogleSignInProps {
  clientId: GoogleClientId;
  /**
   * The sign-in commit — `AuthProvider.onAuthSuccess`, which sets the session,
   * re-runs the route's loaders and only then closes the modal. It returns a
   * promise for that reason, so the type says so; this component does not
   * sequence anything after it and hands it off with `void`.
   */
  onSuccess?: (user?: UserProfile) => void | Promise<void>;
}

/** The `auth.google.*` key each popup outcome renders on the STOREFRONT. */
type PopupFailureKey = 'errorBlocked' | 'errorCancelled' | 'errorPopupBlocked' | 'errorSignIn';

/**
 * Popup outcome → storefront copy.
 *
 * EXHAUSTIVE by construction (`assertNever` on the default), which is the whole
 * structural point of LET-77: a variant added to `GoogleAuthFailure` is a
 * COMPILE error here until someone decides what the storefront says about it.
 * Nothing can drift into `errorCancelled` by omission any more.
 *
 * Lives outside the component (and returns a KEY, not rendered text) so it is a
 * pure function of the failure, independent of the translator and of React.
 */
function popupFailureKey(failure: GoogleAuthFailure): PopupFailureKey {
  switch (failure.kind) {
    case 'blocked':
      // The org_internal / access_denied / admin-policy case. The copy points at
      // phone+OTP, which accepts any number and is the storefront's other real
      // door — the public backend auto-provisions non-company emails as
      // Customers, so nothing about this user is unwelcome, only this credential.
      return 'errorBlocked';
    case 'cancelled':
      // She really did close the popup. Unchanged, and now ONLY this case.
      return 'errorCancelled';
    case 'popupBlocked':
      return 'errorPopupBlocked';
    case 'unavailable':
    case 'unclassified':
      // GIS never loaded, or Google sent something we have no case for. Generic
      // "Google sign-in failed" — honest about not knowing, and pointedly NOT
      // an accusation that the user cancelled.
      return 'errorSignIn';
    default:
      return assertNever(failure);
  }
}

export function GoogleSignIn({ clientId, onSuccess }: GoogleSignInProps) {
  const tShared = useTranslations('auth.shared');
  const tGoogle = useTranslations('auth.google');
  const localizeApiError = useLocalizeApiError();
  const [error, setError] = useState<LocalizedText | null>(null);

  const signInMutation = useMutation({
    mutationFn: async (code: string) => {
      const result = await bff('/api/auth/google', { code });
      if (!result.success) throw bffError(result.error, 'Google sign-in failed');
      return result.data;
    },
    onSuccess: (user) => {
      setError(null);
      // The commit — and the loader re-run it awaits — belongs to the ONE
      // door (`AuthProvider.onAuthSuccess`). This component used to fire its
      // own `router.refresh()` beside it, i.e. a second, un-ordered bridge
      // racing the first; the modal then closed on whichever won.
      void onSuccess?.(user);
    },
    onError: (err) => setError(localizeApiError(err, 'auth.google.errorSignIn')),
  });

  const handleClick = useCallback(() => {
    setError(null);
    requestGoogleAuthCode({ clientId })
      .then((code) => signInMutation.mutate(code))
      .catch((err: unknown) => {
        // TOTAL narrowing: `toGoogleAuthFailure` maps every possible caught
        // value to a variant, so there is no leftover branch here in which the
        // old "everything is a cancellation" collapse could grow back.
        const failure = toGoogleAuthFailure(err);
        clientLogger.error(
          {
            error: err instanceof Error ? err : new Error(String(err)),
            component: 'GoogleSignIn',
            // The reason, on the log line as well as on screen — the console
            // misconfiguration behind LET-77 was diagnosable from Google's
            // `org_internal` alone, and nothing we shipped ever recorded it.
            googleFailureKind: failure.kind,
          },
          'OAuth popup failed'
        );
        setError(tGoogle(popupFailureKey(failure)));
      });
  }, [clientId, signInMutation, tGoogle]);

  if (!clientId) {
    clientLogger.warn(
      { component: 'GoogleSignIn' },
      'clientId not provided — Google Sign-In disabled'
    );
    return null;
  }

  return (
    <div className="space-y-2">
      <AppButton
        variant="secondary"
        fullWidth
        loading={inFlight(signInMutation.isPending)}
        onClick={handleClick}
        type="button"
      >
        <GoogleIcon className="mr-2" />
        {tShared('continueWithProvider', { provider: 'Google' })}
      </AppButton>
      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
